<template>
  <Input
    class="mts-virtual-input"
    :readOnly="inMtsApp"
    v-bind="getBindValue"
    :value="state"
    @input="onInput"
    @press-enter="onEnter"
    @click="!isOpen && (isOpen = true)"
  >
    <template v-for="item in Object.keys($slots)" #[item]="data">
      <slot :name="item" v-bind="data || {}"></slot>
    </template>
  </Input>

  <VirtualKeyboard :open="isOpen" v-bind="{ ...config, ...eventsHandler }" />
</template>

<script setup lang="ts">
  import { Input, InputProps } from 'ant-design-vue';
  import { ComponentOptionsMixin, computed, ref, useAttrs } from 'vue';
  import { type KeyboardValueProps, useKeyboardValue, useRuleFormItem } from '@mts-fe/hooks';
  import { omit } from '@mts-fe/utils';
  import { VirtualKeyboard, type VirtualKeyboardProps } from '@mts/components/Base';
  import { t } from '@mts/hooks/web/useI18n';
  import { inMtsApp } from '@mts/utils';

  type VirtualInputProps = Omit<InputProps, 'value'> &
    KeyboardValueProps & {
      value?: string | number;
      /** 虚拟键盘配置项 */
      config?: VirtualKeyboardProps;
    };

  defineOptions({
    name: 'VirtualInput',
    extends: Input as ComponentOptionsMixin,
    inheritAttrs: false,
  });

  const props = withDefaults(defineProps<VirtualInputProps>(), {
    bordered: true,
    placeholder: 'common.inputText',
    config: () => ({ symbol: '.' }),
  });
  const attrs = useAttrs();
  const getBindValue = computed(() => ({
    ...attrs,
    ...omit(props, ['value', 'onUpdate:value']),
    placeholder: t(props.placeholder.toString()),
    onBlur: closeKeyboard,
  }));

  // Embedded in the form, just use the hook binding to perform form verification
  const [state] = useRuleFormItem(props, 'value', 'change');

  const emit = defineEmits(['update:value', 'change', 'ok']);
  const valueChanged = (value: string) => {
    state.value = value;
    emit('update:value', value);
  };

  const inputRegExp = new RegExp(`[^\\d${props.config?.symbol ?? '.'}]`, 'gi');
  const onInput = (e) => updateValue(e.target.value.replace(inputRegExp, ''));
  const onEnter = (e) => e.target.blur();

  const isOpen = ref(false);
  const closeKeyboard = () => {
    isOpen.value = false;
    emit('ok', state.value);
  };

  const { updateValue, eventsHandler } = useKeyboardValue(props, 'value', {
    valueProps: computed(() => ({ min: props.min, max: props.max, precision: props.precision })),
    onChange: valueChanged,
    onOk: closeKeyboard,
    onClose: closeKeyboard,
  });
</script>
